import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FileText, Download } from 'lucide-react';
import { Message } from '../../types/messages';
import { MessageSummary } from '../ai/MessageSummary';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

// Wrapper component to handle summary loaded callback
function MessageSummaryWrapper({ messages, className, onSummaryLoaded }: { 
  messages: Message[]; 
  className?: string;
  onSummaryLoaded?: () => void;
}) {
  const handleSummaryChanged = (hasSummary: boolean) => {
    if (hasSummary) {
      console.log('[MessageSummaryWrapper] AI summary loaded, triggering scroll');
      onSummaryLoaded?.();
    }
  };
  
  return <MessageSummary messages={messages} className={className} onSummaryChanged={handleSummaryChanged} />;
}

interface MessageListProps {
  messages: Message[];
  currentUserId: string;
  isChannel?: boolean; // Add this to know when to show sender names
  onSummaryLoaded?: () => void; // Callback when AI summary loads
}

export default function MessageList({ messages, currentUserId, isChannel = false, onSummaryLoaded }: MessageListProps) {
  const [senderNames, setSenderNames] = useState<Record<string, string>>({});
  const [showAISummary, setShowAISummary] = useState(false);

  // Fetch sender names for channel messages
  useEffect(() => {
    if (!isChannel) return;

    const fetchSenderNames = async () => {
      const senderIds = [...new Set(messages.map(m => m.senderId))];
      const namesToFetch = senderIds.filter(id => !senderNames[id]);
      
      if (namesToFetch.length === 0) return;

      const namePromises = namesToFetch.map(async (senderId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', senderId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const name = userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown User';
            return [senderId, name] as [string, string];
          }
        } catch (error) {
          console.error('Error fetching user:', error);
        }
        return [senderId, 'Unknown User'] as [string, string];
      });

      const results = await Promise.all(namePromises);
      const newSenderNames = Object.fromEntries(results);
      setSenderNames(prev => ({ ...prev, ...newSenderNames }));
    };

    fetchSenderNames();
  }, [messages, isChannel, senderNames]);

  // Show AI summary when there are 5+ messages
  useEffect(() => {
    setShowAISummary(messages.length >= 5);
  }, [messages.length]);

  const formatMessageDate = (timestamp: any) => {
    try {
      // Handle Firestore timestamp
      if (timestamp?.seconds) {
        return format(new Date(timestamp.seconds * 1000), 'p');
      }
      // Handle string date
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), 'p');
      }
      // Handle Date object
      if (timestamp instanceof Date) {
        return format(timestamp, 'p');
      }
      // Fallback
      return '';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {/* Messages */}
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex ${message.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`max-w-[70%] ${
              message.senderId === currentUserId
                ? 'bg-primary-500 text-white'
                : 'bg-surface-dark text-text-primary'
            } rounded-lg p-3 shadow-sm`}
          >
            {message.isAiSummary && (
              <div className="text-xs font-medium mb-1 text-primary-200">AI Summary</div>
            )}
            
            {/* Show sender name for channel messages (but not for current user's messages) */}
            {isChannel && message.senderId !== currentUserId && (
              <div className={`text-xs font-medium mb-1 ${
                message.senderId === currentUserId ? 'text-primary-200' : 'text-text-secondary'
              }`}>
                {senderNames[message.senderId] || 'Loading...'}
              </div>
            )}
            
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
            
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {message.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 p-2 rounded ${
                      message.senderId === currentUserId
                        ? 'bg-black/10 text-white'
                        : 'bg-white/10 text-text-primary'
                    }`}
                  >
                    <FileText className="w-4 h-4 shrink-0" />
                    <span className="flex-1 truncate text-sm">{attachment.name}</span>
                    <a
                      href={attachment.url}
                      download={attachment.name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-1 rounded transition-colors ${
                        message.senderId === currentUserId
                          ? 'hover:bg-black/20'
                          : 'hover:bg-white/20'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
            
            <div
              className={`text-xs mt-1 ${
                message.senderId === currentUserId ? 'text-primary-200' : 'text-text-secondary'
              }`}
            >
              {formatMessageDate(message.createdAt)}
            </div>
          </div>
        </div>
      ))}

      {messages.length === 0 && (
        <div className="flex items-center justify-center h-full">
          <p className="text-text-secondary">No messages yet</p>
        </div>
      )}

      {/* AI Summary at bottom */}
      {showAISummary && (
        <div className="bg-background/95 backdrop-blur-sm border-t border-border-light mt-4 pt-4">
          <MessageSummaryWrapper 
            messages={messages} 
            className="mx-auto max-w-4xl"
            onSummaryLoaded={onSummaryLoaded}
          />
        </div>
      )}

    </div>
  );
}
